<template>
  <div class="asset-history-container">
    <div class="card">
      <div class="card-header">
        <h5>
          <i class="bi bi-clock-history" />
          {{ $t('components.assetDetails.historyTitle') }}
        </h5>
      </div>
      <div class="card-body">
        <div class="asset-history-filters">
          <div class="d-flex flex-row flex-wrap gap-2 filter-controls">
            <date-picker
              v-model="startDate"
              class="flex-grow"
              :placeholder="$t('components.datepicker.startPlaceholder')"
              :type="'datetime'"
              :range="false"
              :start-time="{ hours: 0, minutes: 0 }"
              :time-picker-inline="true"
              @emitDate="emitStartDate"
            />
            <date-picker
              v-model="endDate"
              class="flex-grow"
              :placeholder="$t('components.datepicker.endPlaceholder')"
              :type="'datetime'"
              :range="false"
              :start-time="{ hours: 0, minutes: 0 }"
              :time-picker-inline="true"
              @emitDate="emitEndDate"
            />
            <button
              v-if="!isSearching"
              class="btn btn-success btn-search"
              @click="searchHistory"
            >
              {{ $t('components.search') }}
            </button>
            <button
              v-else
              class="btn btn-success btn-search btn-search-disabled"
              :title="$t('components.history.tooltip')"
              data-bs-toggle="tooltip"
              disabled
            >
              {{ $t('components.search') }}
            </button>
          </div>
          <div class="quick-range-buttons">
            <span class="quick-range-label">{{ $t('components.assetDetails.quickRangeLabel') }}</span>
            <button
              v-for="preset in rangePresets"
              :key="preset.key"
              type="button"
              class="btn btn-light btn-sm"
              :class="{ active: activePreset === preset.key }"
              @click="applyPreset(preset.key)"
            >
              {{ $t(preset.label) }}
            </button>
          </div>
          <div class="errors">
            <p
              v-if="filterError"
              class="error-msg"
            >
              {{ filterError }}
            </p>
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center py-5"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
          </div>
        </div>

        <div
          v-else-if="error"
          class="alert alert-danger"
        >
          <i class="bi bi-exclamation-triangle" />
          {{ error }}
        </div>

        <div
          v-else-if="chartHasData"
          class="history-chart-wrapper"
        >
          <!-- WAGO: Energy Heatmap (średnia godzinowa) -->
          <template v-if="isWagoAsset">
            <WagoEnergyHeatmap
              :chart-series="heatmapSeries"
              :is-dark-mode="isDarkMode"
              :range-start-ts="heatmapRangeStartTs"
              :range-end-ts="heatmapRangeEndTs"
            />
          </template>

          <!-- WAGO: osobny wykres per kategoria -->
          <template v-if="isWagoAsset">
            <template
              v-for="group in wagoChartGroups"
              :key="group.id"
            >
              <div
                v-if="wagoGroupHasData(group) && wagoGroupHasVisibleSeries(group)"
                class="chart-container chart-container--wago mb-3"
                :class="{ 'dark-mode': isDarkMode }"
              >
                <div class="chart-header">
                  <h3 class="chart-title">
                    <i class="bi bi-graph-up" />
                    <span>{{ $t(group.titleKey) }}</span>
                  </h3>
                  <div class="chart-actions">
                    <button
                      class="btn btn-custom"
                      type="button"
                      @click="resetWagoGroupZoom(group.id)"
                      title="Reset zoom"
                    >
                      <i class="bi bi-arrow-clockwise" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportWagoGroupToPng(group)"
                      title="Eksport PNG"
                    >
                      <i class="bi bi-filetype-png" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportWagoGroupToJpg(group)"
                      title="Eksport JPG"
                    >
                      <i class="bi bi-filetype-jpg" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportWagoGroupCsv(group)"
                      title="Eksport CSV"
                    >
                      <i class="bi bi-filetype-csv" />
                    </button>
                  </div>
                </div>
                <div class="chart-body">
                  <LineChart
                    :key="group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                    :height="wagoChartHeight"
                    :chart-id="'wago-history-chart-' + group.id"
                    :chart-data="wagoChartDataForHistory(group)"
                    :options="wagoChartOptions(group)"
                    :plugins="chartPlugins"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- DKN_LMS: osobny wykres per grupa -->
          <template v-else-if="isDknLmsAsset">
            <template
              v-for="group in dknChartGroups"
              :key="group.id"
            >
              <div
                v-if="dknGroupHasData(group) && dknGroupHasVisibleSeries(group)"
                class="chart-container chart-container--wago mb-3"
                :class="{ 'dark-mode': isDarkMode }"
              >
                <div class="chart-header">
                  <h3 class="chart-title">
                    <i class="bi bi-graph-up" />
                    <span>{{ $t(group.titleKey) }}</span>
                  </h3>
                  <div class="chart-actions">
                    <button
                      class="btn btn-custom"
                      type="button"
                      @click="resetDknGroupZoom(group.id)"
                      title="Reset zoom"
                    >
                      <i class="bi bi-arrow-clockwise" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportDknGroupToPng(group)"
                      title="Eksport PNG"
                    >
                      <i class="bi bi-filetype-png" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportDknGroupToJpg(group)"
                      title="Eksport JPG"
                    >
                      <i class="bi bi-filetype-jpg" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportDknGroupCsv(group)"
                      title="Eksport CSV"
                    >
                      <i class="bi bi-filetype-csv" />
                    </button>
                  </div>
                </div>
                <div class="chart-body">
                  <LineChart
                    :key="'dkn-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                    :height="wagoChartHeight"
                    :chart-id="'dkn-history-chart-' + group.id"
                    :chart-data="dknChartDataForHistory(group)"
                    :options="dknChartOptions(group)"
                    :plugins="chartPlugins"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- DKN_UART (Daikin Mini Zeas): osobny wykres per grupa -->
          <template v-else-if="isDknUartDetected">
            <div
              v-if="!isDknUartAsset"
              class="history-placeholder"
            >
              <i class="bi bi-inbox" />
              <p>{{ $t('components.assetDetails.noDataInRange') }}</p>
            </div>
            <template
              v-for="group in uartChartGroups"
              :key="group.id"
            >
              <div
                v-if="dknGroupHasData(group) && dknGroupHasVisibleSeries(group)"
                class="chart-container chart-container--wago mb-3"
                :class="{ 'dark-mode': isDarkMode }"
              >
                <div class="chart-header">
                  <h3 class="chart-title">
                    <i class="bi bi-graph-up" />
                    <span>{{ $t(group.titleKey) }}</span>
                  </h3>
                  <div class="chart-actions">
                    <button
                      class="btn btn-custom"
                      type="button"
                      @click="resetUartGroupZoom(group.id)"
                      title="Reset zoom"
                    >
                      <i class="bi bi-arrow-clockwise" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportUartGroupToPng(group)"
                      title="Eksport PNG"
                    >
                      <i class="bi bi-filetype-png" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportUartGroupToJpg(group)"
                      title="Eksport JPG"
                    >
                      <i class="bi bi-filetype-jpg" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportUartGroupCsv(group)"
                      title="Eksport CSV"
                    >
                      <i class="bi bi-filetype-csv" />
                    </button>
                  </div>
                </div>
                <div class="chart-body">
                  <LineChart
                    :key="'uart-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                    :height="wagoChartHeight"
                    :chart-id="'uart-history-chart-' + group.id"
                    :chart-data="dknChartDataForHistory(group)"
                    :options="dknChartOptions(group)"
                    :plugins="chartPlugins"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- DKN_MPS: osobny wykres per grupa -->
          <template v-else-if="isDknMpsAsset">
            <template
              v-for="group in mpsChartGroups"
              :key="group.id"
            >
              <div
                v-if="dknGroupHasData(group) && dknGroupHasVisibleSeries(group)"
                class="chart-container chart-container--wago mb-3"
                :class="{ 'dark-mode': isDarkMode }"
              >
                <div class="chart-header">
                  <h3 class="chart-title">
                    <i class="bi bi-graph-up" />
                    <span>{{ $t(group.titleKey) }}</span>
                  </h3>
                  <div class="chart-actions">
                    <button
                      class="btn btn-custom"
                      type="button"
                      @click="resetMpsGroupZoom(group.id)"
                      title="Reset zoom"
                    >
                      <i class="bi bi-arrow-clockwise" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportMpsGroupToPng(group)"
                      title="Eksport PNG"
                    >
                      <i class="bi bi-filetype-png" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportMpsGroupToJpg(group)"
                      title="Eksport JPG"
                    >
                      <i class="bi bi-filetype-jpg" />
                    </button>
                    <button
                      class="btn btn-custom chart-action-export"
                      type="button"
                      @click="exportMpsGroupCsv(group)"
                      title="Eksport CSV"
                    >
                      <i class="bi bi-filetype-csv" />
                    </button>
                  </div>
                </div>
                <div class="chart-body">
                  <LineChart
                    :key="'mps-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                    :height="wagoChartHeight"
                    :chart-id="'mps-history-chart-' + group.id"
                    :chart-data="dknChartDataForHistory(group)"
                    :options="dknChartOptions(group)"
                    :plugins="chartPlugins"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- Non-WAGO: oryginalny pojedynczy wykres -->
          <template v-else-if="!isChartFullscreen">
            <div
              class="chart-container"
              :class="{ 'dark-mode': isDarkMode }"
            >
              <div class="chart-buttons-floating">
                <button
                  class="btn btn-custom"
                  type="button"
                  @click="openChartFullscreen"
                  title="Pełny ekran"
                >
                  <i class="bi bi-arrows-fullscreen" />
                </button>
                <button
                  class="btn btn-custom"
                  type="button"
                  @click="resetChartZoom"
                  title="Reset zoom"
                >
                  <i class="bi bi-arrow-clockwise" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportToPng"
                  title="Eksport PNG"
                >
                  <i class="bi bi-filetype-png" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportToJpg"
                  title="Eksport JPG"
                >
                  <i class="bi bi-filetype-jpg" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportCsv"
                  title="Eksport CSV"
                >
                  <i class="bi bi-filetype-csv" />
                </button>
              </div>
              <LineChart
                ref="historyChart"
                :key="chartRenderKey"
                :height="550"
                :chart-id="chartId"
                :chart-data="chartData"
                :options="chartOptions"
                :plugins="chartPlugins"
              />
            </div>
          </template>

          <!-- WAGO: osobna tabela per grupa, poniżej wszystkich wykresów -->
          <template v-if="isWagoAsset">
            <template
              v-for="group in wagoChartGroups"
              :key="'table-' + group.id"
            >
              <div
                v-if="wagoGroupHasData(group)"
                class="series-table-wrapper"
              >
                <div class="series-table-group-title">
                  {{ $t(group.titleKey) }}
                </div>
                <DxDataGrid
                  class="series-grid"
                  :data-source="wagoGroupSeriesStats(group)"
                  :show-borders="false"
                  :row-alternation-enabled="true"
                  :column-auto-width="true"
                  :column-hiding-enabled="true"
                  :hover-state-enabled="false"
                >
                  <DxColumn
                    data-field="visible"
                    :caption="$t('components.assetDetails.seriesTable.visible')"
                    cell-template="visibility-cell"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="label"
                    :caption="$t('components.assetDetails.seriesTable.series')"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="min"
                    :caption="$t('components.assetDetails.seriesTable.min')"
                    cell-template="min-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="avg"
                    :caption="$t('components.assetDetails.seriesTable.avg')"
                    cell-template="avg-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="max"
                    :caption="$t('components.assetDetails.seriesTable.max')"
                    cell-template="max-chip"
                    alignment="center"
                  />
                  <template #min-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-primary fw-semibold">{{ data.data.min }}</span>
                    </div>
                  </template>
                  <template #avg-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-info text-white fw-semibold">{{ data.data.avg }}</span>
                    </div>
                  </template>
                  <template #max-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-danger fw-semibold">{{ data.data.max }}</span>
                    </div>
                  </template>
                  <template #visibility-cell="{ data }">
                    <div class="visibility-cell">
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`series-switch-${data.data.key}`"
                          :checked="data.data.visible"
                          @change="(e) => onSeriesVisibilityToggle(data.data.key, (e.target as HTMLInputElement).checked)"
                        >
                        <label
                          class="form-check-label"
                          :for="`series-switch-${data.data.key}`"
                        />
                      </div>
                      <span :class="data.data.visible ? 'status-visible' : 'status-hidden'">
                        {{ data.data.visible ? $t('components.assetDetails.seriesTable.visibleLabel') : $t('components.assetDetails.seriesTable.hiddenLabel') }}
                      </span>
                    </div>
                  </template>
                </DxDataGrid>
              </div>
            </template>
          </template>

          <!-- DKN_LMS: osobna tabela per grupa, poniżej wszystkich wykresów -->
          <template v-else-if="isDknLmsAsset">
            <template
              v-for="group in dknChartGroups"
              :key="'dkn-table-' + group.id"
            >
              <div
                v-if="dknGroupHasData(group)"
                class="series-table-wrapper"
              >
                <div class="series-table-group-title">
                  {{ $t(group.titleKey) }}
                </div>
                <DxDataGrid
                  class="series-grid"
                  :data-source="dknGroupSeriesStats(group)"
                  :show-borders="false"
                  :row-alternation-enabled="true"
                  :column-auto-width="true"
                  :column-hiding-enabled="true"
                  :hover-state-enabled="false"
                >
                  <DxColumn
                    data-field="visible"
                    :caption="$t('components.assetDetails.seriesTable.visible')"
                    cell-template="visibility-cell"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="label"
                    :caption="$t('components.assetDetails.seriesTable.series')"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="min"
                    :caption="$t('components.assetDetails.seriesTable.min')"
                    cell-template="min-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="avg"
                    :caption="$t('components.assetDetails.seriesTable.avg')"
                    cell-template="avg-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="max"
                    :caption="$t('components.assetDetails.seriesTable.max')"
                    cell-template="max-chip"
                    alignment="center"
                  />
                  <template #min-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-primary fw-semibold">{{ data.data.min }}</span>
                    </div>
                  </template>
                  <template #avg-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-info text-white fw-semibold">{{ data.data.avg }}</span>
                    </div>
                  </template>
                  <template #max-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-danger fw-semibold">{{ data.data.max }}</span>
                    </div>
                  </template>
                  <template #visibility-cell="{ data }">
                    <div class="visibility-cell">
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`dkn-series-switch-${data.data.key}`"
                          :checked="data.data.visible"
                          @change="(e) => onSeriesVisibilityToggle(data.data.key, (e.target as HTMLInputElement).checked)"
                        >
                        <label
                          class="form-check-label"
                          :for="`dkn-series-switch-${data.data.key}`"
                        />
                      </div>
                      <span :class="data.data.visible ? 'status-visible' : 'status-hidden'">
                        {{ data.data.visible ? $t('components.assetDetails.seriesTable.visibleLabel') : $t('components.assetDetails.seriesTable.hiddenLabel') }}
                      </span>
                    </div>
                  </template>
                </DxDataGrid>
              </div>
            </template>
          </template>

          <!-- DKN_UART: osobna tabela per grupa, poniżej wszystkich wykresów -->
          <template v-else-if="isDknUartDetected">
            <template
              v-for="group in uartChartGroups"
              :key="'uart-table-' + group.id"
            >
              <div
                v-if="dknGroupHasData(group)"
                class="series-table-wrapper"
              >
                <div class="series-table-group-title">
                  {{ $t(group.titleKey) }}
                </div>
                <DxDataGrid
                  class="series-grid"
                  :data-source="dknGroupSeriesStats(group)"
                  :show-borders="false"
                  :row-alternation-enabled="true"
                  :column-auto-width="true"
                  :column-hiding-enabled="true"
                  :hover-state-enabled="false"
                >
                  <DxColumn
                    data-field="visible"
                    :caption="$t('components.assetDetails.seriesTable.visible')"
                    cell-template="visibility-cell"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="label"
                    :caption="$t('components.assetDetails.seriesTable.series')"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="min"
                    :caption="$t('components.assetDetails.seriesTable.min')"
                    cell-template="min-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="avg"
                    :caption="$t('components.assetDetails.seriesTable.avg')"
                    cell-template="avg-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="max"
                    :caption="$t('components.assetDetails.seriesTable.max')"
                    cell-template="max-chip"
                    alignment="center"
                  />
                  <template #min-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-primary fw-semibold">{{ data.data.min }}</span>
                    </div>
                  </template>
                  <template #avg-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-info text-white fw-semibold">{{ data.data.avg }}</span>
                    </div>
                  </template>
                  <template #max-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-danger fw-semibold">{{ data.data.max }}</span>
                    </div>
                  </template>
                  <template #visibility-cell="{ data }">
                    <div class="visibility-cell">
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`uart-series-switch-${data.data.key}`"
                          :checked="data.data.visible"
                          @change="(e) => onSeriesVisibilityToggle(data.data.key, (e.target as HTMLInputElement).checked)"
                        >
                        <label
                          class="form-check-label"
                          :for="`uart-series-switch-${data.data.key}`"
                        />
                      </div>
                      <span :class="data.data.visible ? 'status-visible' : 'status-hidden'">
                        {{ data.data.visible ? $t('components.assetDetails.seriesTable.visibleLabel') : $t('components.assetDetails.seriesTable.hiddenLabel') }}
                      </span>
                    </div>
                  </template>
                </DxDataGrid>
              </div>
            </template>
          </template>

          <!-- DKN_MPS: osobna tabela per grupa, poniżej wszystkich wykresów -->
          <template v-else-if="isDknMpsAsset">
            <template
              v-for="group in mpsChartGroups"
              :key="'mps-table-' + group.id"
            >
              <div
                v-if="dknGroupHasData(group)"
                class="series-table-wrapper"
              >
                <div class="series-table-group-title">
                  {{ $t(group.titleKey) }}
                </div>
                <DxDataGrid
                  class="series-grid"
                  :data-source="dknGroupSeriesStats(group)"
                  :show-borders="false"
                  :row-alternation-enabled="true"
                  :column-auto-width="true"
                  :column-hiding-enabled="true"
                  :hover-state-enabled="false"
                >
                  <DxColumn
                    data-field="visible"
                    :caption="$t('components.assetDetails.seriesTable.visible')"
                    cell-template="visibility-cell"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="label"
                    :caption="$t('components.assetDetails.seriesTable.series')"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="min"
                    :caption="$t('components.assetDetails.seriesTable.min')"
                    cell-template="min-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="avg"
                    :caption="$t('components.assetDetails.seriesTable.avg')"
                    cell-template="avg-chip"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="max"
                    :caption="$t('components.assetDetails.seriesTable.max')"
                    cell-template="max-chip"
                    alignment="center"
                  />
                  <template #min-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-primary fw-semibold">{{ data.data.min }}</span>
                    </div>
                  </template>
                  <template #avg-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-info text-white fw-semibold">{{ data.data.avg }}</span>
                    </div>
                  </template>
                  <template #max-chip="{ data }">
                    <div class="text-center">
                      <span class="badge rounded-pill bg-danger fw-semibold">{{ data.data.max }}</span>
                    </div>
                  </template>
                  <template #visibility-cell="{ data }">
                    <div class="visibility-cell">
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`mps-series-switch-${data.data.key}`"
                          :checked="data.data.visible"
                          @change="(e) => onSeriesVisibilityToggle(data.data.key, (e.target as HTMLInputElement).checked)"
                        >
                        <label
                          class="form-check-label"
                          :for="`mps-series-switch-${data.data.key}`"
                        />
                      </div>
                      <span :class="data.data.visible ? 'status-visible' : 'status-hidden'">
                        {{ data.data.visible ? $t('components.assetDetails.seriesTable.visibleLabel') : $t('components.assetDetails.seriesTable.hiddenLabel') }}
                      </span>
                    </div>
                  </template>
                </DxDataGrid>
              </div>
            </template>
          </template>

          <!-- Non-WAGO: jedna wspólna tabela -->
          <div
            v-else
            class="series-table-wrapper"
          >
            <DxDataGrid
              class="series-grid"
              :data-source="seriesStats"
              :show-borders="false"
              :row-alternation-enabled="true"
              :column-auto-width="true"
              :column-hiding-enabled="true"
              :hover-state-enabled="false"
            >
              <DxColumn
                data-field="visible"
                :caption="$t('components.assetDetails.seriesTable.visible')"
                cell-template="visibility-cell"
                :allow-sorting="false"
                :allow-filtering="false"
                alignment="center"
              />
              <DxColumn
                data-field="label"
                :caption="$t('components.assetDetails.seriesTable.series')"
                alignment="center"
              />
              <DxColumn
                data-field="min"
                :caption="$t('components.assetDetails.seriesTable.min')"
                cell-template="min-chip"
                alignment="center"
              />
              <DxColumn
                data-field="avg"
                :caption="$t('components.assetDetails.seriesTable.avg')"
                cell-template="avg-chip"
                alignment="center"
              />
              <DxColumn
                data-field="max"
                :caption="$t('components.assetDetails.seriesTable.max')"
                cell-template="max-chip"
                alignment="center"
              />
              <template #min-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-primary fw-semibold">
                    {{ data.data.min }}
                  </span>
                </div>
              </template>
              <template #avg-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-info text-white fw-semibold">
                    {{ data.data.avg }}
                  </span>
                </div>
              </template>
              <template #max-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-danger fw-semibold">
                    {{ data.data.max }}
                  </span>
                </div>
              </template>
              <template #visibility-cell="{ data }">
                <div class="visibility-cell">
                  <div class="form-check form-switch m-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`series-switch-${data.data.key}`"
                      :checked="data.data.visible"
                      @change="(e) => onSeriesVisibilityToggle(data.data.key, (e.target as HTMLInputElement).checked)"
                    >
                    <label
                      class="form-check-label"
                      :for="`series-switch-${data.data.key}`"
                    />
                  </div>
                  <span :class="data.data.visible ? 'status-visible' : 'status-hidden'">
                    {{ data.data.visible ? $t('components.assetDetails.seriesTable.visibleLabel') : $t('components.assetDetails.seriesTable.hiddenLabel') }}
                  </span>
                </div>
              </template>
            </DxDataGrid>
          </div>
        </div>

        <div
          v-else
          class="history-placeholder"
        >
          <i class="bi bi-inbox" />
          <p>{{ $t('components.assetDetails.noHistory') }}</p>
        </div>
      </div>
    </div>
  </div>

  <teleport
    v-if="!isWagoAsset"
    to="body"
  >
    <div
      v-if="isChartFullscreen"
      class="asset-history-container chart-fullscreen-overlay"
      :class="{ 'dark-mode': isDarkMode }"
      @click.self="closeChartFullscreen"
    >
      <div
        class="chart-container chart-container-fullscreen"
        :class="{ 'dark-mode': isDarkMode }"
      >
        <div class="chart-buttons-floating">
          <button
            class="btn btn-custom"
            type="button"
            @click="closeChartFullscreen"
            title="Zamknij"
          >
            <i class="bi bi-x-lg" />
          </button>
          <button
            class="btn btn-custom"
            type="button"
            @click="resetChartZoom"
            title="Reset zoom"
          >
            <i class="bi bi-arrow-clockwise" />
          </button>
          <button
            class="btn btn-custom"
            type="button"
            @click="exportToPng"
            title="Eksport PNG"
          >
            <i class="bi bi-filetype-png" />
          </button>
          <button
            class="btn btn-custom"
            type="button"
            @click="exportToJpg"
            title="Eksport JPG"
          >
            <i class="bi bi-filetype-jpg" />
          </button>
          <button
            class="btn btn-custom"
            type="button"
            @click="exportCsv"
            title="Eksport CSV"
          >
            <i class="bi bi-filetype-csv" />
          </button>
        </div>
        <LineChart
          ref="historyChart"
          :key="chartRenderKey"
          :height="fullscreenChartHeight"
          :chart-id="chartId"
          :chart-data="chartData"
          :options="chartOptions"
          :plugins="chartPlugins"
        />
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import dayjs from 'dayjs'
import { LineChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'

import DatePicker from '@/components/extras/DatePicker.vue'
import WagoEnergyHeatmap from '@/components/content/WagoEnergyHeatmap.vue'
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid'
import { getAssetHistoryRange } from '@/crud/asset/getters'
import { resetZoom, exportToImg } from '@/utils/chart'
import { generateCsv, getHumanDate } from '@/utils/tools'
import {
  buildSeriesRecord,
  clipSeriesRecordByTimeRange,
  DKN_LMS_CHART_GROUPS,
  DKN_MPS_CHART_GROUPS,
  DKN_UART_CHART_GROUPS,
  GATEWAY_SERIES_KEYS,
  SERIES_KEYS,
  SERIES_META,
  WAGO_CHART_GROUPS,
  type ChartSeriesRecord,
  type DknChartGroup,
  type SeriesKey,
  type WagoChartGroup
} from '@/utils/asset-history-series'

Chart.register(...registerables)
Chart.register(zoomPlugin)

const RANGE_PRESETS = [
  { key: 'today', label: 'components.assetDetails.rangePresets.today' },
  { key: 'yesterday', label: 'components.assetDetails.rangePresets.yesterday' },
  { key: 'last24h', label: 'components.assetDetails.rangePresets.last24h' },
  { key: 'last7days', label: 'components.assetDetails.rangePresets.last7days' }
] as const

type RangePresetKey = typeof RANGE_PRESETS[number]['key']


const MODBUS_SERIES_KEYS: SeriesKey[] = [
  'wagoEnergyActive',
  'wagoEnergyReactive',
  'wagoPowerL1',
  'wagoPowerL2',
  'wagoPowerL3',
  'wagoCurrentL1',
  'wagoCurrentL2',
  'wagoCurrentL3',
  'wagoVoltLNL1',
  'wagoVoltLNL2',
  'wagoVoltLNL3',
  'wagoVoltLLL1L2',
  'wagoVoltLLL2L3',
  'wagoVoltLLL1L3',
  'wagoPfL1',
  'wagoPfL2',
  'wagoPfL3'
]

const MODBUS_DEFAULT_VISIBLE = new Set<SeriesKey>([
  'wagoEnergyActive',
  'wagoEnergyReactive',
  'wagoPowerL1',
  'wagoVoltLLL1L2'
])

const DKN_LMS_SERIES_KEYS: SeriesKey[] = Array.from(
  new Set(DKN_LMS_CHART_GROUPS.flatMap((group) => group.keys))
) as SeriesKey[]

const DKN_LMS_DEFAULT_VISIBLE = new Set<SeriesKey>([
  'dknLmsSv',
  'dknLmsSc',
  'dknLmsSh',
  'dknLmsTgs',
  'dknLmsTeu',
  'dknLmsSdt'
])

const DKN_MPS_SERIES_KEYS: SeriesKey[] = Array.from(
  new Set(DKN_MPS_CHART_GROUPS.flatMap((group) => group.keys))
) as SeriesKey[]

const DKN_MPS_DEFAULT_VISIBLE = new Set<SeriesKey>([
  'dknMpsProbe1',
  'dknMpsSetpoint',
  'dknMpsStatusOnOff'
])

const DKN_UART_SERIES_KEYS: SeriesKey[] = Array.from(
  new Set(DKN_UART_CHART_GROUPS.flatMap((group) => group.keys))
) as SeriesKey[]

const DKN_UART_DEFAULT_VISIBLE = new Set<SeriesKey>([
  'dknUartD20OutdoorAirTemp',
  'dknUartD21HighPressure',
  'dknUartD30CompressorFreq',
  'dknUartD10OperationMode'
])

const DKN_LMS_TABLE_KEYS = new Set<SeriesKey>([
  'dknLmsSv',
  'dknLmsSc',
  'dknLmsSh',
  'dknLmsVsr',
  'dknLmsTgs',
  'dknLmsTeu',
  'dknLmsSdt'
])

const DEFAULT_SERIES_KEYS: SeriesKey[] = [
  'temperature',
  'humidity',
  'powerSupply',
  'batteryVoltage',
  'batteryStatus'
]

const DEVICE_SERIES_KEYS: SeriesKey[] = [
  'xAxis',
  'yAxis',
  'zAxis',
  'magnitude',
  'magnitudeNoGravity'
]
export default defineComponent({
  name: 'AssetHistory',
  components: {
    DatePicker,
    LineChart,
    WagoEnergyHeatmap,
    DxDataGrid,
    DxColumn
  },
  props: {
    assetId: {
      type: [String, Number] as PropType<string | number>,
      required: true
    }
  },
  setup() {
    return {
      rangePresets: RANGE_PRESETS,
      wagoChartGroups: WAGO_CHART_GROUPS,
      dknChartGroups: DKN_LMS_CHART_GROUPS,
      mpsChartGroups: DKN_MPS_CHART_GROUPS,
      uartChartGroups: DKN_UART_CHART_GROUPS
    }
  },
  data() {
    const defaultEnd = dayjs()
    return {
      loading: true,
      error: null as string | null,
      filterError: '',
      startDate: defaultEnd.subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
      endDate: defaultEnd.format('YYYY-MM-DD HH:mm'),
      isSearching: false,
      chartSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
      heatmapSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
      seriesVisibility: {
        temperature: true,
        humidity: true,
        powerSupply: false,
        batteryVoltage: false,
        batteryStatus: false,
        xAxis: true,
        yAxis: true,
        zAxis: true,
        magnitude: true,
        magnitudeNoGravity: true,
        wagoEnergyActive: false,
        wagoEnergyReactive: false,
        wagoPowerL1: false,
        wagoPowerL2: false,
        wagoPowerL3: false,
        wagoCurrentL1: false,
        wagoCurrentL2: false,
        wagoCurrentL3: false,
        wagoVoltLNL1: false,
        wagoVoltLNL2: false,
        wagoVoltLNL3: false,
        wagoVoltLLL1L2: false,
        wagoVoltLLL2L3: false,
        wagoVoltLLL1L3: false,
        wagoPfL1: false,
        wagoPfL2: false,
        wagoPfL3: false,
        dknLmsSv: false,
        dknLmsSc: false,
        dknLmsDfs: false,
        dknLmsAlr: false,
        dknLmsVsr: false,
        dknLmsSh: false,
        dknLmsPpu: false,
        dknLmsShb: false,
        dknLmsPpub: false,
        dknLmsTgs: false,
        dknLmsTeu: false,
        dknLmsTeub: false,
        dknLmsTgsb: false,
        dknLmsSdt: false,
        dknLmsSdtb: false,
        dknLmsScb: false,
        dknLmsSt: false,
        dknLmsAh: false,
        dknLmsAl: false,
        dknLmsR4: false,
        dknLmsD2: false,
        dknLmsDfm: false,
        dknLmsDas: false,
        dknLmsCht: false,
        dknLmsFia: false,
        dknLmsFoi: false,
        dknLmsFot: false,
        dknLmsGlo: false,
        dknLmsHi: false,
        dknLmsU1: false,
        dknMpsProbe1: false,
        dknMpsProbe2: false,
        dknMpsDisplayedProbe: false,
        dknMpsTempRegProbe: false,
        dknMpsSetpoint: false,
        dknMpsSetpointActive: false,
        dknMpsStatusDefrost: false,
        dknMpsStatusOnOff: false,
        dknMpsStatusCooling: false,
        dknMpsDiDoor: false,
        dknMpsDoComp1: false,
        dknMpsDoComp2: false,
        dknMpsDoEvapFan: false,
        dknMpsAlarmHighTemp: false,
        dknMpsAlarmLowTemp: false,
        devPowerSupply: false,
        dknUartD10OperationMode: false,
        dknUartD10ThermostatOnOff: false,
        dknUartD10Defrost: false,
        dknUartD10DemandSignal: false,
        dknUartD10RestartStandby: false,
        dknUartD10OilReturn: false,
        dknUartD10PressureEqualizing: false,
        dknUartD10ModeCompletion: false,
        dknUartD10LowNoiseControl: false,
        dknUartD10ErrorType: false,
        dknUartD10ErrorCode: false,
        dknUartD10SoftwareVersion: false,
        dknUartD10TargetEvapPress: false,
        dknUartD10DischargeTempDrop: false,
        dknUartD10DischargeTempRetries: false,
        dknUartD10CompInvCurrentDrop: false,
        dknUartD10CompInvCurrentRetries: false,
        dknUartD10HpDrop: false,
        dknUartD10HpRetries: false,
        dknUartD10LpDrop: false,
        dknUartD10LpRetries: false,
        dknUartD10FinTempDrop: false,
        dknUartD10FinTempRetries: false,
        dknUartD10OtherDrop: false,
        dknUartD20OutdoorAirTemp: false,
        dknUartD20Ti: false,
        dknUartD20Tl: false,
        dknUartD20Tg: false,
        dknUartD20Td1: false,
        dknUartD20LowPressure1: false,
        dknUartD20LowPressure1T: false,
        dknUartD21HighPressure: false,
        dknUartD21HighPressureT: false,
        dknUartD21InvCurrent: false,
        dknUartD21InvFinTemp: false,
        dknUartD30CompressorFreq: false,
        dknUartD30Fan1: false,
        dknUartD30Fan2: false,
        dknUartD30R1: false,
        dknUartD30Warning: false,
        dknUartD30BackUp: false,
        dknUartD30Ev2: false,
        dknUartD30Ev3: false,
        dknUartD50OperationMode: false,
        dknUartD50ErrorCode: false,
        dknUartD50Setpoint: false,
        dknUartD50SuctionTemp: false,
        dknUartD50LiquidTemp: false,
        dknUartD50GasTemp: false,
        dknUartD50IndoorEv: false,
        dknUartD70Ti: false,
        dknUartD70Tg: false,
        dknUartD70Td: false,
        dknUartD70LowPressure: false,
        dknUartD70LowPressureT: false,
        dknUartD70CompressorFreq: false,
        dknUartSuctionSh: false,
        dknUartDischargeSh: false
      } as Record<SeriesKey, boolean>,
      wagoChartHeight: 320,
      isDarkMode: false,
      themeObserver: null as MutationObserver | null,
      isChartFullscreen: false,
      fullscreenChartHeight: 720,
      bodyOverflowBeforeFullscreen: '' as string,
      activePreset: null as RangePresetKey | null,
      isDknUartDetected: false as boolean
    }
  },
  computed: {
    isWagoAsset(): boolean {
      return MODBUS_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)
    },
    isDknUartAsset(): boolean {
      return DKN_UART_SERIES_KEYS.some(
        (key) => !GATEWAY_SERIES_KEYS.has(key) && this.chartSeries[key].points.length > 0
      )
    },
    isDknMpsAsset(): boolean {
      return !this.isDknUartAsset && DKN_MPS_SERIES_KEYS.some(
        (key) => !GATEWAY_SERIES_KEYS.has(key) && this.chartSeries[key].points.length > 0
      )
    },
    isDknLmsAsset(): boolean {
      return DKN_LMS_SERIES_KEYS.some(
        (key) => !GATEWAY_SERIES_KEYS.has(key) && this.chartSeries[key].points.length > 0
      )
    },
    chartId(): string {
      return `asset-history-chart-${this.assetId}`
    },
    heatmapRangeStartTs(): number | null {
      const parsed = dayjs(this.startDate)
      return parsed.isValid() ? parsed.valueOf() : null
    },
    heatmapRangeEndTs(): number | null {
      const parsed = dayjs(this.endDate)
      return parsed.isValid() ? parsed.valueOf() : null
    },
    chartRenderKey(): string {
      return `${this.isDarkMode ? 'dark' : 'light'}-${this.isChartFullscreen ? 'fullscreen' : 'inline'}`
    },
    activeSeriesKeys(): SeriesKey[] {
      if (this.isDknUartAsset) {
        return DKN_UART_SERIES_KEYS
      }
      if (this.isDknMpsAsset) {
        return DKN_MPS_SERIES_KEYS
      }
      if (this.isDknLmsAsset) {
        return DKN_LMS_SERIES_KEYS
      }
      if (MODBUS_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)) {
        return MODBUS_SERIES_KEYS
      }
      if (DEVICE_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)) {
        return DEVICE_SERIES_KEYS
      }
      return DEFAULT_SERIES_KEYS
    },
    chartHasData(): boolean {
      if (this.isDknUartDetected) return true
      return this.activeSeriesKeys.some((key) => this.chartSeries[key].points.length)
    },
    chartData(): ChartData<'line'> {
      const datasets: Array<any> = []

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        const seriesKey = key as SeriesKey
        const series = this.chartSeries?.[seriesKey]
        if (series && series.points && series.points.length) {
          datasets.push({
            data: series.points,
            parsing: false,
            normalized: true,
            yAxisID: meta.axisId,
            label: series?.label || (this.$t(meta.labelKey) as string),
            borderColor: meta.color,
            backgroundColor: meta.color,
            borderWidth: 2,
            tension: 0.1,
            spanGaps: true,
            hidden: !this.seriesVisibility[seriesKey],
            customKey: seriesKey
          })
        }
      })

      return { datasets }
    },
    chartOptions(): ChartOptions<'line'> {
      const axisDisplay = this.getAxisDisplayMap()
      const axes: Record<string, any> = {
        x: {
          type: 'time',
          time: {
            tooltipFormat: 'DD/MM HH:mm',
            displayFormats: {
              millisecond: 'HH:mm:ss',
              second: 'HH:mm:ss',
              minute: 'DD/MM HH:mm',
              hour: 'DD/MM HH:mm',
              day: 'DD/MM'
            }
          },
          ticks: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.7)' : undefined
          },
          grid: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.05)' : undefined
          }
        }
      }

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        if (axes[meta.axisId]) return

        const seriesKey = key as SeriesKey
        const isBatteryAxis = meta.axisId === 'batteryStatus'
        const isHumidityAxis = meta.axisId === 'humidity'
        const isBatteryVoltageAxis = meta.axisId === 'batteryVoltage'
        const isDknTempAxis = meta.axisId === 'dknTemp'
        const isDknStatusAxis = meta.axisId === 'dknStatus'
        const axisTitle = meta.axisId === 'axis'
          ? this.$t('components.assetDetails.axisTitleDevice') as string
          : (meta.axisTitle
            ? (meta.axisTitle.startsWith('components.') ? (this.$t(meta.axisTitle) as string) : meta.axisTitle)
            : (this.$t(meta.labelKey) as string))

        axes[meta.axisId] = {
          type: 'linear',
          position: meta.axisPosition,
          display: axisDisplay[meta.axisId] ?? this.shouldDisplayAxis(seriesKey),
          min: isBatteryAxis ? -0.1 : (isHumidityAxis || isBatteryVoltageAxis ? 0 : (isDknStatusAxis ? 0 : undefined)),
          max: isBatteryAxis ? 1.1 : (isHumidityAxis ? 100 : (isDknStatusAxis ? 1 : undefined)),
          reverse: isBatteryAxis,
          offset: meta.offset ?? false,
          title: {
            display: true,
            text: axisTitle,
            color: meta.color,
            font: { weight: 'bold' }
          },
          ticks: {
            color: meta.color,
            font: { weight: 'bold' },
            stepSize: isBatteryAxis ? 1 : (isHumidityAxis ? 10 : (isDknTempAxis ? 5 : (isDknStatusAxis ? 1 : undefined))),
            callback: (value: string | number) => {
              const numeric = typeof value === 'string' ? parseFloat(value) : value
              if (!Number.isFinite(numeric)) {
                return ''
              }
              if (isBatteryAxis) {
                if (numeric === 0) return this.$t('common.ok') as string
                if (numeric === 1) return this.$t('common.bad') as string
                return ''
              }
              if (isDknStatusAxis) {
                const rounded = Math.round(numeric)
                return rounded >= 1 ? '1' : '0'
              }
              return meta.tickFormatter ? meta.tickFormatter(numeric) : numeric
            }
          },
          grid: {
            drawOnChartArea: meta.axisId === 'temperature' || meta.axisId === 'powerSupply',
            color: this.isDarkMode ? `${meta.color.replace('0.85', '0.2')}` : undefined
          }
        }
      })

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: axes,
        plugins: {
          decimation: {
            enabled: true,
            algorithm: 'lttb',
            samples: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: this.isDarkMode ? { size: 14, weight: 'bold' } : undefined
            },
            onClick: (_event, legendItem, legend) => {
              const datasetIndex = legendItem.datasetIndex
              if (typeof datasetIndex !== 'number') return

              const dataset = legend.chart.data.datasets[datasetIndex] as any
              const seriesKey = dataset?.customKey as SeriesKey | undefined
              if (!seriesKey) return

              const nextVisibility = !this.seriesVisibility[seriesKey]
              this.onSeriesVisibilityToggle(seriesKey, nextVisibility, legend.chart)
            }
          },
          title: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const dataset = context.dataset as any
                const seriesKey = dataset?.customKey as SeriesKey | undefined
                const label = context.dataset?.label ?? ''
                const value = context.parsed.y
                const formattedValue = this.formatSeriesValue(seriesKey, value)
                const unit = seriesKey ? SERIES_META[seriesKey]?.unit || '' : ''
                return `${label}: ${formattedValue}${unit ? ' ' + unit : ''}`
              }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x'
            },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x'
            }
          }
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 5
          }
        },
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 10
          }
        }
      }
    },
    chartPlugins(): any[] {
      const bgColor = this.isDarkMode ? '#1e1e2d' : '#f5f8fa'
      return [
        {
          id: 'assetHistoryBackground',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx
            ctx.save()
            ctx.fillStyle = bgColor
            ctx.fillRect(0, 0, chart.width, chart.height)
            ctx.restore()
          }
        }
      ]
    },
    seriesStats(): Array<any> {
      const excludedFromTable: SeriesKey[] = ['powerSupply']
      const isDknLms = this.activeSeriesKeys === DKN_LMS_SERIES_KEYS
      const allowedKeys = isDknLms
        ? this.activeSeriesKeys.filter((key) => DKN_LMS_TABLE_KEYS.has(key))
        : this.activeSeriesKeys

      return Object.entries(SERIES_META)
        .map(([key, meta]) => ({ key: key as SeriesKey, meta }))
        .filter(({ key }) => !excludedFromTable.includes(key))
        .filter(({ key }) => allowedKeys.includes(key))
        .filter(({ key }) => this.hasSeriesData(key))
        .map(({ key, meta }) => {
          const series = this.chartSeries[key]
          const numericValues = series.points.map(point => point.y)
          const min = numericValues.length ? Math.min(...numericValues) : null
          const max = numericValues.length ? Math.max(...numericValues) : null
          const avg = numericValues.length ? numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length : null

          return {
            key,
            label: series?.label || (this.$t(meta.labelKey) as string) as string,
            min: this.formatStatValue(key, min),
            avg: this.formatStatValue(key, avg),
            max: this.formatStatValue(key, max),
            visible: this.seriesVisibility[key]
          }
        })
    }
  },
  mounted() {
    this.checkDarkMode()
    this.observeTheme()
    this.fetchHistory()
  },
  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
    this.closeChartFullscreen()
  },
  methods: {
    wagoGroupSeriesStats(group: WagoChartGroup): Array<any> {
      const groupKeySet = new Set(group.keys)
      return this.seriesStats.filter((row) => groupKeySet.has(row.key))
    },
    wagoGroupHasData(group: WagoChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    wagoGroupHasVisibleSeries(group: WagoChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0 && this.seriesVisibility[key])
    },

    wagoChartDataForHistory(group: WagoChartGroup) {
      const multiplier = group.yMultiplier ?? 1
      return {
        datasets: group.keys
          .filter((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
          .map((key) => {
            const meta = SERIES_META[key]
            const series = this.chartSeries[key]
            const data = multiplier !== 1
              ? series.points.map((p) => ({ x: p.x, y: p.y * multiplier }))
              : series.points
            return {
              data,
              parsing: false,
              normalized: true,
              yAxisID: group.axisPerKey?.[key]?.axisId ?? 'y',
              label: series.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              tension: 0.1,
              spanGaps: true,
              hidden: !this.seriesVisibility[key],
              customKey: key
            } as any
          })
      }
    },
    wagoChartOptions(group: WagoChartGroup) {
      const isPowerFactor = group.id === 'power-factor'
      const isCurrent = group.id === 'current'
      const isDark = this.isDarkMode

      const buildYScale = (unit: string, position: 'left' | 'right', minY?: number, maxY?: number, stepSize?: number) => ({
        type: 'linear',
        position,
        min: minY,
        max: maxY,
        title: { display: false },
        ticks: {
          color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined,
          callback: (value: any) => {
            const numeric = typeof value === 'number' ? value : parseFloat(String(value))
            if (!Number.isFinite(numeric)) return value
            return unit ? `${numeric} ${unit}` : String(numeric)
          },
          stepSize: stepSize ?? (isPowerFactor ? 0.1 : (isCurrent ? 0.05 : (unit === 'V' ? 5 : undefined)))
        },
        grid: { color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined }
      })

      const yScales: Record<string, any> = {}
      if (group.axisPerKey) {
        const seenAxes = new Set<string>()
        group.keys.forEach((key) => {
          const axisConfig = group.axisPerKey![key]
          if (!axisConfig || seenAxes.has(axisConfig.axisId)) return
          seenAxes.add(axisConfig.axisId)
          yScales[axisConfig.axisId] = buildYScale(axisConfig.unit, axisConfig.position, undefined, undefined, axisConfig.stepSize)
        })
      } else {
        yScales['y'] = buildYScale(group.unit, 'left', group.minY, group.maxY)
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'DD/MM HH:mm',
              displayFormats: { millisecond: 'HH:mm:ss', second: 'HH:mm:ss', minute: 'DD/MM HH:mm', hour: 'DD/MM HH:mm', day: 'DD/MM' }
            },
            grid: { color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined },
            ticks: { color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined }
          },
          ...yScales
        },
        plugins: {
          decimation: {
            enabled: true,
            algorithm: 'lttb',
            samples: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: { size: 13, weight: 600 },
              padding: 12,
              usePointStyle: true
            },
            onClick: (_event: any, legendItem: any, legend: any) => {
              const datasetIndex = legendItem.datasetIndex
              if (typeof datasetIndex !== 'number') return
              const dataset = legend.chart.data.datasets[datasetIndex] as any
              const seriesKey = dataset?.customKey as SeriesKey | undefined
              if (!seriesKey) return
              this.onSeriesVisibilityToggle(seriesKey, !this.seriesVisibility[seriesKey], legend.chart)
            }
          },
          zoom: {
            pan: { enabled: true, mode: 'x' },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                let unit: string
                if (group.axisPerKey) {
                  const axisId = context.dataset.yAxisID
                  const axisConfig = Object.values(group.axisPerKey).find((c) => c?.axisId === axisId)
                  unit = axisConfig?.unit || ''
                } else {
                  unit = group.unit || ''
                }
                const value = context.parsed.y
                const label = context.dataset.label || ''
                return unit ? `${label}: ${value} ${unit}` : `${label}: ${value}`
              }
            }
          }
        },
        showLine: true,
        interaction: { mode: 'index', intersect: false },
        elements: { point: { radius: 0, hitRadius: 5 } },
        layout: { padding: { left: 20, right: 20, top: 20, bottom: 10 } }
      }
    },
    exportWagoGroupToPng(group: WagoChartGroup) {
      exportToImg('image/png', `wago-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportWagoGroupToJpg(group: WagoChartGroup) {
      exportToImg('image/jpeg', `wago-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportWagoGroupCsv(group: WagoChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `wago_${group.id}_asset_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      const locale = (this.$i18n?.locale as string) || 'en'
      generateCsv(exportRows, filename, this.$t, locale)
    },
    dknGroupSeriesStats(group: DknChartGroup): Array<any> {
      return group.keys
        .filter((key) => this.hasSeriesData(key))
        .map((key) => {
          const meta = SERIES_META[key]
          const series = this.chartSeries[key]
          const numericValues = series.points.map(point => point.y)
          const min = numericValues.length ? Math.min(...numericValues) : null
          const max = numericValues.length ? Math.max(...numericValues) : null
          const avg = numericValues.length ? numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length : null

          return {
            key,
            label: series?.label || (this.$t(meta.labelKey) as string),
            min: this.formatStatValue(key, min),
            avg: this.formatStatValue(key, avg),
            max: this.formatStatValue(key, max),
            visible: this.seriesVisibility[key]
          }
        })
    },
    dknGroupHasData(group: DknChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    dknGroupHasVisibleSeries(group: DknChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0 && this.seriesVisibility[key])
    },
    dknGroupActiveKeys(group: DknChartGroup): SeriesKey[] {
      return group.keys.filter((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    dknStatusTimelineLaneLabel(_key: SeriesKey, fallback: string): string {
      return fallback
    },
    dknChartDataForHistory(group: DknChartGroup): ChartData<'line'> {
      const activeKeys = this.dknGroupActiveKeys(group)
      const groupMode = group.mode ?? 'line'

      if (groupMode === 'statusTimeline') {
        return {
          datasets: activeKeys.map((key, index) => {
            const meta = SERIES_META[key]
            const series = this.chartSeries[key]
            const laneOffset = index * 2
            return {
              data: (series?.points || []).map((p) => ({
                x: p.x,
                y: (Math.round(Number(p.y)) >= 1 ? 1 : 0) + laneOffset,
                rawY: p.y
              })),
              parsing: false,
              normalized: true,
              yAxisID: 'dknStatusTimeline',
              label: series?.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              stepped: true,
              tension: 0,
              spanGaps: true,
              hidden: !this.seriesVisibility[key],
              customKey: key,
              pointRadius: 0,
              pointHitRadius: 5
            } as any
          })
        }
      }

      if (groupMode === 'virtualProbe') {
        const key = activeKeys[0]
        if (!key) return { datasets: [] }
        const meta = SERIES_META[key]
        const series = this.chartSeries[key]
        return {
          datasets: [
            {
              data: series?.points || [],
              parsing: false,
              normalized: true,
              yAxisID: meta.axisId,
              label: series?.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              tension: 0.1,
              spanGaps: true,
              hidden: !this.seriesVisibility[key],
              customKey: key
            } as any
          ]
        }
      }

      return {
        datasets: activeKeys.map((key) => {
          const meta = SERIES_META[key]
          const series = this.chartSeries[key]
          const isStep = groupMode === 'step'
          const isSetpoints = group.id === 'setpoints'
          const dashed = isSetpoints && key !== 'dknLmsSt'
          return {
            data: series?.points || [],
            parsing: false,
            normalized: true,
            yAxisID: meta.axisId,
            label: series?.label || (this.$t(meta.labelKey) as string),
            borderColor: meta.color,
            backgroundColor: meta.color,
            borderWidth: 2,
            tension: isStep ? 0 : 0.1,
            stepped: isStep,
            spanGaps: true,
            hidden: !this.seriesVisibility[key],
            customKey: key,
            borderDash: dashed ? [7, 5] : undefined
          } as any
        })
      }
    },
    dknChartOptions(group: DknChartGroup): ChartOptions<'line'> {
      const isDark = this.isDarkMode
      const activeKeys = this.dknGroupActiveKeys(group)
      const groupMode = group.mode ?? 'line'
      const isStatusTimelineGroup = groupMode === 'statusTimeline'
      const isFanOutputsGroup = group.id === 'fan-outputs'
      const isPercentGroup = group.keys.length > 0 && group.keys.every((key) => SERIES_META[key].unit === '%')
      const fanEnabledLabel = this.$t('components.assetDetails.dknTooltipStates.enabled') as string
      const fanDisabledLabel = this.$t('components.assetDetails.dknTooltipStates.disabled') as string

      const scales: any = {
        x: {
          type: 'time',
          time: {
            tooltipFormat: 'DD/MM/YYYY HH:mm',
            displayFormats: {
              millisecond: 'HH:mm:ss',
              second: 'HH:mm:ss',
              minute: 'HH:mm',
              hour: 'DD/MM HH:mm',
              day: 'DD/MM'
            }
          },
          grid: {
            color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined
          },
          ticks: {
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined
          }
        }
      }

      const axisMeta: Record<string, { title: string; showTitle: boolean; position: 'left' | 'right'; color: string; offset?: boolean; unit: string }> = {}
      const laneLabels = activeKeys.map((key) => {
        const fullLabel = this.chartSeries[key]?.label || (this.$t(SERIES_META[key].labelKey) as string)
        return this.dknStatusTimelineLaneLabel(key, fullLabel)
      })

      if (isStatusTimelineGroup) {
        const maxLane = Math.max(1, activeKeys.length * 2)
        const capturedVisibility = this.seriesVisibility
        scales.dknStatusTimeline = {
          type: 'linear',
          position: 'left',
          min: -0.5,
          max: maxLane - 0.5,
          title: {
            display: false
          },
          ticks: {
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
            autoSkip: false,
            stepSize: 1,
            font: { size: 10 },
            callback: function (value: any) {
              const numeric = typeof value === 'number' ? value : parseFloat(String(value))
              if (!Number.isFinite(numeric)) return ''
              for (let idx = 0; idx < laneLabels.length; idx++) {
                const laneCenter = idx * 2 + 1
                if (Math.abs(numeric - laneCenter) < 0.2) {
                  if (capturedVisibility[activeKeys[idx]] === false) return ''
                  return laneLabels[idx] || ''
                }
              }
              return ''
            }
          },
          grid: {
            color: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'
          }
        }
      } else {
        activeKeys.forEach((key) => {
          const meta = SERIES_META[key]
          if (!axisMeta[meta.axisId]) {
            const showTitle = !!meta.axisTitle
            const title = meta.axisTitle && meta.axisTitle.startsWith('components.')
              ? (this.$t(meta.axisTitle) as string)
              : (meta.axisTitle || '')
            axisMeta[meta.axisId] = {
              title,
              showTitle,
              position: meta.axisPosition,
              color: meta.color,
              offset: meta.offset,
              unit: meta.unit
            }
          }
        })

        Object.entries(axisMeta).forEach(([axisId, meta]) => {
          const isHz = meta.unit === 'Hz'
          const isTemperature = meta.unit === '°C'
          const isKelvin = meta.unit === 'K'
          const isAlarmCountGroup = group.id === 'alarm-count'
          const isIntegerCountAxis = axisId === 'dknUartCount'

          scales[axisId] = {
            type: 'linear',
            position: meta.position,
            offset: meta.offset,
            min: isPercentGroup ? 0 : (isIntegerCountAxis ? 0 : undefined),
            max: isPercentGroup ? 100 : undefined,
            title: {
              display: meta.showTitle,
              text: meta.title,
              color: meta.color,
              font: { weight: 'bold' }
            },
            ticks: {
              color: meta.color,
              font: { weight: 'bold' },
              stepSize: isAlarmCountGroup || isIntegerCountAxis ? 1 : (isPercentGroup ? 5 : (isHz ? 5 : (isTemperature || isKelvin ? 5 : undefined))),
              callback: function (value: any) {
                const numeric = typeof value === 'number' ? value : parseFloat(String(value))
                if (!Number.isFinite(numeric)) return value
                return meta.unit ? `${numeric} ${meta.unit}` : `${numeric}`
              }
            },
            grid: {
              drawOnChartArea: meta.position === 'right',
              color: isDark ? 'rgba(255, 255, 255, 0.06)' : undefined
            }
          }
        })
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales,
        plugins: {
          decimation: {
            enabled: !isStatusTimelineGroup,
            algorithm: 'lttb',
            samples: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: { size: 13, weight: 600 },
              padding: 12,
              usePointStyle: true
            },
            onClick: (_event: any, legendItem: any, legend: any) => {
              const datasetIndex = legendItem.datasetIndex
              if (typeof datasetIndex !== 'number') return
              const dataset = legend.chart.data.datasets[datasetIndex] as any
              const seriesKey = dataset?.customKey as SeriesKey | undefined
              if (!seriesKey) return
              this.onSeriesVisibilityToggle(seriesKey, !this.seriesVisibility[seriesKey], legend.chart)
            }
          },
          zoom: {
            pan: { enabled: true, mode: 'x' },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }
          },
          tooltip: {
            callbacks: {
              label: function (context: any) {
                const label = context.dataset.label || ''
                const rawPoint = context.raw || {}
                if (isStatusTimelineGroup) {
                  const rawValue = typeof rawPoint?.rawY === 'number'
                    ? rawPoint.rawY
                    : (Math.round(Number(context.parsed.y)) % 2)
                  const isActive = Math.round(Number(rawValue)) >= 1
                  if (isFanOutputsGroup) {
                    return `${label}: ${isActive ? fanEnabledLabel : fanDisabledLabel}`
                  }
                  return `${label}: ${isActive ? '1' : '0'}`
                }
                const value = typeof rawPoint?.rawY === 'number' ? rawPoint.rawY : context.parsed.y
                const axisId = context.dataset.yAxisID
                const unit = axisMeta[axisId]?.unit || ''
                return unit ? `${label}: ${value} ${unit}` : `${label}: ${value}`
              }
            }
          }
        },
        showLine: true,
        interaction: {
          mode: isStatusTimelineGroup ? 'nearest' : 'index',
          intersect: false
        },
        elements: { point: { radius: 0, hitRadius: 5 } },
        layout: { padding: { left: 20, right: 20, top: 20, bottom: 10 } }
      }
    },
    exportDknGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `dkn-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportDknGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `dkn-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportDknGroupCsv(group: DknChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `dkn_${group.id}_asset_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      const locale = (this.$i18n?.locale as string) || 'en'
      generateCsv(exportRows, filename, this.$t, locale)
    },
    resetMpsGroupZoom(groupId: string) {
      resetZoom(`mps-history-chart-${groupId}`)
    },
    resetUartGroupZoom(groupId: string) {
      resetZoom(`uart-history-chart-${groupId}`)
    },
    exportUartGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `uart-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportUartGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `uart-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportUartGroupCsv(group: DknChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `uart_${group.id}_asset_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      const locale = (this.$i18n?.locale as string) || 'en'
      generateCsv(exportRows, filename, this.$t, locale)
    },
    exportMpsGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `mps-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportMpsGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `mps-history-chart-${group.id}`, this.assetId as string, this.isDarkMode)
    },
    exportMpsGroupCsv(group: DknChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `mps_${group.id}_asset_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      const locale = (this.$i18n?.locale as string) || 'en'
      generateCsv(exportRows, filename, this.$t, locale)
    },
    emitStartDate(value: string | string[]) {
      if (Array.isArray(value)) {
        this.startDate = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.startDate = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    emitEndDate(value: string | string[]) {
      if (Array.isArray(value)) {
        this.endDate = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.endDate = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    validateRange(): boolean {
      if (!this.startDate || !this.endDate) {
        this.filterError = this.$t('customValidators.allMustBeCompleted') as string
        return false
      }

      const start = dayjs(this.startDate)
      const end = dayjs(this.endDate)

      if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
        this.filterError = this.$t('components.searchInterval') as string
        return false
      }

      const diffDays = end.diff(start, 'day', true)
      if (diffDays > 90) {
        this.filterError = this.$t('components.searchInterval') as string
        return false
      }

      this.filterError = ''
      return true
    },
    searchHistory() {
      if (!this.validateRange()) return
      this.fetchHistory(true)
    },
    applyPreset(key: RangePresetKey) {
      this.activePreset = key
      const now = dayjs()

      switch (key) {
        case 'today': {
          const start = dayjs().startOf('day')
          const end = dayjs().endOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'yesterday': {
          const start = dayjs().subtract(1, 'day').startOf('day')
          const end = dayjs().subtract(1, 'day').endOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'last24h': {
          const end = now
          const start = now.subtract(24, 'hours')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'last7days': {
          const end = dayjs().endOf('day')
          const start = dayjs().subtract(6, 'day').startOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        default:
          this.activePreset = null
      }
    },
    applyModbusVisibilityDefaults() {
      const hasModbusData = MODBUS_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)
      if (!hasModbusData) return

      const next = { ...this.seriesVisibility }
      MODBUS_SERIES_KEYS.forEach((key) => {
        next[key] = MODBUS_DEFAULT_VISIBLE.has(key)
      })
      this.seriesVisibility = next
    },

    applyDknLmsVisibilityDefaults() {
      const hasDknData = DKN_LMS_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)
      if (!hasDknData) return

      const next = { ...this.seriesVisibility }
      DKN_LMS_SERIES_KEYS.forEach((key) => {
        next[key] = DKN_LMS_DEFAULT_VISIBLE.has(key)
      })
      this.seriesVisibility = next
    },
    applyDknMpsVisibilityDefaults() {
      const hasMpsData = DKN_MPS_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)
      if (!hasMpsData) return

      const next = { ...this.seriesVisibility }
      DKN_MPS_SERIES_KEYS.forEach((key) => {
        next[key] = DKN_MPS_DEFAULT_VISIBLE.has(key)
      })
      this.seriesVisibility = next
    },
    applyDknUartVisibilityDefaults() {
      const hasUartData = DKN_UART_SERIES_KEYS.some((key) => this.chartSeries[key].points.length > 0)
      if (!hasUartData) return

      const next = { ...this.seriesVisibility }
      DKN_UART_SERIES_KEYS.forEach((key) => {
        next[key] = DKN_UART_DEFAULT_VISIBLE.has(key)
      })
      this.seriesVisibility = next
    },

    async fetchHistory(isSearch = false) {
      if (isSearch) {
        this.isSearching = true
      }
      this.loading = true
      this.error = null
      this.heatmapSeries = buildSeriesRecord({}, (key) => key)

      try {
        const source = typeof this.$route?.query?.source === 'string' ? this.$route.query.source : undefined
        const dateFromIso = this.getIsoDate(this.startDate)
        const dateToIso = this.getIsoDate(this.endDate)
        const rangeStartMs = dayjs(dateFromIso).valueOf()
        const rangeEndMs = dayjs(dateToIso).valueOf()
        const heatmapDateFromIso = dayjs(dateFromIso).startOf('hour').subtract(1, 'hour').toISOString()

        const response = await getAssetHistoryRange(
          this.assetId,
          heatmapDateFromIso,
          dateToIso,
          source === 'device' ? 'device' : undefined
        )

        this.isDknUartDetected = Object.keys((response.data?.series ?? {}) as Record<string, unknown>).some((k) => k.startsWith('dknUart'))

        const fetchedSeries = buildSeriesRecord(
          response.data?.series,
          (key) => this.$t(key) as string
        )
        this.heatmapSeries = fetchedSeries
        this.chartSeries = clipSeriesRecordByTimeRange(fetchedSeries, rangeStartMs, rangeEndMs)
        this.applyModbusVisibilityDefaults()
        this.applyDknLmsVisibilityDefaults()
        this.applyDknMpsVisibilityDefaults()
        this.applyDknUartVisibilityDefaults()
        this.$nextTick(() => {
          this.syncChartVisibility()
        })
      } catch (err: any) {
        console.error('Error loading asset history', err)
        this.error = this.$t('components.assetDetails.errorLoading') as string
      } finally {
        this.loading = false
        this.isSearching = false
      }
    },
    getIsoDate(date: string): string {
      const parsed = dayjs(date)
      if (!parsed.isValid()) {
        return dayjs().toISOString()
      }
      return parsed.toISOString()
    },
    hasSeriesData(key: SeriesKey): boolean {
      return this.chartSeries[key].points.length > 0
    },
    formatSeriesValue(key: SeriesKey | undefined, value: number | string | null): string {
      if (value === null) return ''

      if (key === 'batteryStatus') {
        const numeric = typeof value === 'number' ? value : parseFloat(String(value))
        if (Number.isNaN(numeric)) return String(value)
        return numeric === 0 ? this.$t('common.ok') as string : this.$t('common.bad') as string
      }

      if (key === 'powerSupply' || key === 'batteryVoltage') {
        const numeric = typeof value === 'number' ? value : parseFloat(String(value))
        if (Number.isNaN(numeric)) return String(value)
        return numeric.toFixed(2)
      }
      if (typeof value === 'number') return value.toString()
      const numeric = parseFloat(String(value))
      return Number.isNaN(numeric) ? String(value) : numeric.toString()
    },
    formatStatValue(key: SeriesKey, value: number | null): string {
      if (value === null || Number.isNaN(value)) {
        return '—'
      }

      if (key === 'batteryStatus') {
        return '—'
      }

      const precision = this.getSeriesPrecision(key)
      const unit = SERIES_META[key]?.unit || ''
      const formatted = precision !== null ? value.toFixed(precision) : value.toString()
      return unit ? `${formatted} ${unit}` : formatted
    },
    getSeriesPrecision(key: SeriesKey): number | null {
      if (key === 'temperature' || key === 'humidity') {
        return 1
      }
      if (key === 'powerSupply' || key === 'batteryVoltage') {
        return 2
      }
      if (key === 'batteryStatus') {
        return null
      }
      const unit = SERIES_META[key]?.unit || ''
      if (unit === '°C' || unit === 'K' || unit === '%' || unit === 'Hz') {
        return 1
      }
      if (unit === '') {
        return 0
      }
      return 2
    },
    getAxisDisplayMap(): Record<string, boolean> {
      const axisDisplay: Record<string, boolean> = {}
      SERIES_KEYS.forEach((seriesKey) => {
        const meta = SERIES_META[seriesKey]
        const isVisible = this.shouldDisplayAxis(seriesKey)
        axisDisplay[meta.axisId] = axisDisplay[meta.axisId] || isVisible
      })
      return axisDisplay
    },
    shouldDisplayAxis(key: SeriesKey): boolean {
      return this.hasSeriesData(key) && this.seriesVisibility[key]
    },
    setSeriesVisibility(key: SeriesKey, isVisible: boolean) {
      this.seriesVisibility = {
        ...this.seriesVisibility,
        [key]: isVisible
      }
    },
    onSeriesVisibilityToggle(key: SeriesKey, isVisible: boolean, chart?: any) {
      this.setSeriesVisibility(key, isVisible)
      this.syncChartVisibility(chart)
    },
    syncChartVisibility(chart?: any) {
      const chartInstance = chart || this.getChartInstance()
      if (!chartInstance) return

      const axisDisplay = this.getAxisDisplayMap()

      SERIES_KEYS.forEach((seriesKey) => {
        const isVisible = this.shouldDisplayAxis(seriesKey)
        const datasetIndex = chartInstance.data.datasets.findIndex((dataset: any) => dataset?.customKey === seriesKey)
        if (datasetIndex !== -1) {
          chartInstance.setDatasetVisibility(datasetIndex, isVisible)
        }
      })

      const scales = (chartInstance.options as any)?.scales
      if (scales) {
        Object.entries(axisDisplay).forEach(([axisId, isVisible]) => {
          if (scales[axisId]) {
            scales[axisId].display = isVisible
          }
        })
      }

      chartInstance.update()
    },
    getChartInstance(): any {
      const chartComponent = this.$refs.historyChart as any
      return chartComponent?.chartInstance || chartComponent?.$?.chart?.chartInstance
    },
    resetChartZoom() {
      resetZoom(this.chartId)
    },
    resetWagoGroupZoom(groupId: string) {
      resetZoom(`wago-history-chart-${groupId}`)
    },
    resetDknGroupZoom(groupId: string) {
      resetZoom(`dkn-history-chart-${groupId}`)
    },
    exportToPng() {
      exportToImg('image/png', this.chartId, this.assetId as string, this.isDarkMode)
    },
    exportToJpg() {
      exportToImg('image/jpeg', this.chartId, this.assetId as string, this.isDarkMode)
    },
    exportCsv() {
      if (!this.chartHasData) return

      const exportRows: any[] = []
      Object.entries(this.chartSeries).forEach(([key, series]) => {
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })

      if (!exportRows.length) return

      const filename = `asset_${this.assetId}_history_${getHumanDate(Date.now() / 1000)}`
      const locale = (this.$i18n?.locale as string) || 'en'
      generateCsv(exportRows, filename, this.$t, locale)
    },
    checkDarkMode() {
      this.isDarkMode = document.body.classList.contains('dark-theme')
    },
    openChartFullscreen() {
      if (this.isChartFullscreen) return
      this.isChartFullscreen = true
      this.bodyOverflowBeforeFullscreen = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.updateFullscreenChartHeight()
      window.addEventListener('resize', this.updateFullscreenChartHeight)
      window.addEventListener('orientationchange', this.updateFullscreenChartHeight)
      document.addEventListener('keydown', this.onFullscreenKeydown)
      this.$nextTick(() => {
        this.syncChartVisibility()
      })
    },
    closeChartFullscreen() {
      if (!this.isChartFullscreen) return
      this.isChartFullscreen = false
      document.body.style.overflow = this.bodyOverflowBeforeFullscreen || ''
      window.removeEventListener('resize', this.updateFullscreenChartHeight)
      window.removeEventListener('orientationchange', this.updateFullscreenChartHeight)
      document.removeEventListener('keydown', this.onFullscreenKeydown)
      this.$nextTick(() => {
        this.syncChartVisibility()
      })
    },
    onFullscreenKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.closeChartFullscreen()
      }
    },
    updateFullscreenChartHeight() {
      const padding = 140
      this.fullscreenChartHeight = Math.max(320, window.innerHeight - padding)
    },
    observeTheme() {
      this.themeObserver = new MutationObserver(() => {
        this.checkDarkMode()
      })
      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-history-view.scss';
</style>
